import { useMemo, useCallback, useEffect, useState } from 'react';
import { Badge, Col, Row, Form } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';

import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';

import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';

import UserVacanciesApi from 'libs/api/cv/userVacancies';
import UserVacanciesAdapter from './userVacancies.adapter';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from '../../../../hooks/useUser';
import Helpers from '../../../../helpers';
import FormHelper from '../../../../helpers/FormHelper';
import CVFilters from '../../../../libs/api/cv/cv';
import { getShops, getInactiveShops } from '../../../../hooks/useShops';
import { EditIcon } from '../../../../icons';
import moment from 'moment';

const api = new UserVacanciesApi();
const filtersApi = new CVFilters();

export const statusColors = {
  status_expired: 'black',
  status_new: 'yellow',
  status_to_admin: 'yellow',
  status_confirmed: 'green',
  status_refused: 'red',
  status_deactivate: 'black',
  status_pending: 'yellow',
};

/**
 *
 * @param {boolean} isMainPage - true if the page loads all items, false if just one
 * @returns {{
 *  columns: any,
 *  data: any,
 *  singleItem: any,
 *  loading: boolean,
 *  updateInProcess: boolean,
 *  setUpdateInProcess: func,
 *  onDelete: func,
 *  onChangeTableFilter: func
 * }} state and callbacks for UserVacancies page
 */
const useUserVacancies = (isMainPage = true) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const { id } = useParams();
  const [filtersData, setFiltersData] = useState({
    vacancy_categories: [],
    experience_years: [],
    birthday: [],
    contract_types: [],
    regions: [],
    status: [],
    vacancy_application_types: [],
    vacancy_languages: [],
    roles: [],
  });
  const {
    handleApprove,
    loading,
    data,
    singleItem,
    onDelete,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    handleDownloadExcel,
    fetchList,
  } = useApi({
    resourceKey: 'user_vacancies',
    singleResourceKey: 'user_vacancy',
    api,
    isMainPage,
    adapter: UserVacanciesAdapter,
  });
  const statuses = useMemo(
    () => ({
      status_expired: t('Expired'),
      status_new: t('DA approvare'),
      status_to_admin: t('Escalate to admin'),
      status_confirmed: t('Approvato'),
      status_refused: t('Rifiutato'),
      status_deactivate: t('Disabilitato'),
      status_pending: t('DA approvare'),
    }),
    [t],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
        render: (data, row) => row?.outlet_village,
      },
      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => {},
        render: (data, row) => row?.company,
      },
      {
        title: t('Ruolo'),
        dataIndex: 'user_role',
        sorter: () => {},
      },
      {
        title: t('Nome e cognome'),
        dataIndex: 'user.name',
        sorter: () => {},
        render: (data, row) => row?.user,
      },
      {
        title: 'Stato',
        dataIndex: 'status',
        // width: 200,
        sorter: () => {},
        render: (data, row) => (
          <Badge color={statusColors[data]} text={statuses[data]} />
        ),
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 500,
        fixed: 'right',
        render: (data, row) => (
          <Row gutter={16} justify={'end'} onClick={(e) => e.stopPropagation()}>
            <Col>
              <Link to={`${pathname}/${row.id}/edit`}>
                <Button type='secondary' icon={<EditIcon />} />
              </Link>
            </Col>

            <Col>
              <Link to={`${pathname}/${row.id}`}>
                <Button icon={<EyeOutlined />}>Apri</Button>
              </Link>
            </Col>
            {row.can_deactivate && (
              <Col>
                <Link to={`${pathname}/${row.id}/deactivate`}>
                  <Button type={'red'}>
                    Richiesta disabilitazione funzioni curriculum
                  </Button>
                </Link>
              </Col>
            )}
          </Row>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, statuses]);
  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'input',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: shops,
        inactiveValues: inactiveShops,
      },
      {
        title: t('Ruolo'),
        type: 'select',
        name: 'by_role',
        values: filtersData.roles.map((i) => ({
          value: i.value,
          title: i.title,
        })),
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_text',
        values: [],
        isActiveSearch: true,
      },
    ],
    [t, shops, inactiveShops, shoppingCenters],
  );

  const handleUploadVacancy = useCallback(
    async (params) => {
      setUpdateInProcess(true);

      const data = {
        status: singleItem?.status,
        file_name: params?.type?.file?.name,
        file: params?.type?.file,
        type: params?.type?.file?.type,
        size: params?.type?.file?.size,
      };

      try {
        await api.createUserVacancy(data);
        navigate(UIHelper.getListRoute(pathname, 1));
      } catch (e) {
        const errors = e.response.data || [];
        form.setFields(ErrorHelper.getErrorsForForm(errors));
      } finally {
        setUpdateInProcess(false);
      }
    },
    [form, navigate, pathname, setUpdateInProcess, singleItem],
  );

  const handleDeactivate = async (id) => {
    const data = {
      type_approve: 'deactivate',
    };
    setUpdateInProcess(true);
    try {
      await api.approve(id, data);
      await fetchList();
      navigate(UIHelper.getListRoute(pathname));
    } catch (e) {
      console.log(e);
    } finally {
      setUpdateInProcess(false);
    }
  };

  const handleCreateOrUpdateShop = async (values) => {
    setUpdateInProcess(true);
    try {
      const formData = new FormData();
      for (let key in values) {
        if (key !== 'doc') {
          formData.append(`user_vacancy[${key}]`, values[key] || '');
        } else {
          if (values[key] && values[key][0]?.originFileObj) {
            formData.append(
              `user_vacancy[${key}]`,
              values[key][0]?.originFileObj || '',
            );
          }
        }
      }

      if (!id) {
        await api.createVacanice(formData);
      } else {
        await api.update(id, formData);
      }

      navigate(UIHelper.getListRoute(pathname, 1));
    } catch (e) {
      form.setFields(
        ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
          shopping_center: 'shopping_center_id',
        }),
      );
      if (e.response.status === 500) {
        UIHelper.serverError();
      }
    } finally {
      setUpdateInProcess(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const filters = await filtersApi.list();
        setFiltersData(filters.data);
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);

  useEffect(() => {
    let fieldsValues = [];
    for (let key in singleItem) {
      switch (key) {
        case 'shopping_center': {
          fieldsValues.push({
            name: 'shopping_center_id',
            value: singleItem[key]?.id,
          });
          break;
        }
      }
    }
    form.setFields(fieldsValues);
  }, [singleItem]);

  const handleDownloadPdf = useCallback(
    async (url, fileName = 'vacancy_application.pdf') => {
      setUpdateInProcess(true);
      try {
        const response = await api.getPDF(url);
        Helpers.downloadFile(response, fileName);
      } catch (e) {
        UIHelper.serverError();
      } finally {
        setUpdateInProcess(false);
      }
    },
    [],
  );

  return {
    form,
    columns,
    data,
    singleItem,
    filters,
    loading,
    onDelete,
    statuses,
    updateInProcess,
    onChangeTableFilter,
    handleUploadVacancy,
    handleApprove,
    handleDeactivate,
    handleCreateOrUpdateShop,
    shoppingCenters,
    handleDownloadExcel,
    handleDownloadPdf,
  };
};

export default useUserVacancies;
